/**
 * O modal "Nova Organização" do botão abaixo de "Organização ativa" (spec 2026-10-27 §4.4).
 *
 * Com `acao='pedir'` termina em "Enviar pedido": o pedido vira chamado no tenant de quem pede,
 * atendido pela DEVOPS, e a tela mostra o protocolo. Com `acao='criar'` termina em "Criar" e segue
 * o caminho de criação de hoje (`POST /v1/organizations`, o mesmo da tela admin); a criação direta
 * com chamado auto-aprovado é a Onda 4.
 *
 * A prontidão é só leitura e quem decide é o servidor: a tela mostra o que ele conferiu, as
 * recusas (que travam o envio) e o que "depende de terceiro" (o grupo GitLab, que só avisa).
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Anchor, Button, Group, List, Loader, Modal, Select, Stack, Text, TextInput, Textarea, ThemeIcon,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  ROTULO_DO_TIPO, abrirPedido, lerProntidao, motivoDaRecusa,
  type Disponibilidade, type PedidoAberto, type Prontidao as ProntidaoDoPedido, type TipoDePedido,
} from '@/api/pedidosDeOrganizacao'
import type { ContaPrdResultado } from '@/api/organizations'
import { useCreateOrganization, useGestores } from '@/hooks/useOrganizations'
import { useAuthStore } from '@/store/auth'
import { CampoSlugDoProjeto, SLUG_PROJETO, slugDoNome } from './CampoSlugDoProjeto'

const JUSTIFICATIVA_MIN = 10
const JUSTIFICATIVA_MAX = 200

const ROTULO_DO_TERCEIRO: Record<string, string> = { grupo_gitlab: 'Grupo GitLab' }

interface Props {
  opened: boolean
  onClose: () => void
  tenantId: number
  disponibilidade: Disponibilidade
}

type Desfecho =
  | { tipo: 'pedido'; pedido: PedidoAberto }
  | { tipo: 'criada'; nome: string; conta: ContaPrdResultado | undefined }

export function ModalNovaOrganizacao({ opened, onClose, tenantId, disponibilidade }: Props) {
  const { acao, tipos } = disponibilidade
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const { data: gestores } = useGestores()

  const [tipo, setTipo] = useState<TipoDePedido>(tipos.includes('projeto_novo') ? 'projeto_novo' : tipos[0])
  const [nome, setNome] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEditado, setSlugEditado] = useState(false)
  const [responsavel, setResponsavel] = useState<string | null>(null)
  const [justificativa, setJustificativa] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [desfecho, setDesfecho] = useState<Desfecho | null>(null)

  // Responsável: quem pede, se tiver a função gestor (`create_organization` recusa quem não tem).
  const quemPedeEGestor = userId != null && (gestores ?? []).some((g) => g.id === userId)
  useEffect(() => {
    if (responsavel == null && quemPedeEGestor) setResponsavel(String(userId))
  }, [quemPedeEGestor, responsavel, userId])

  const semAws = tipo === 'sem_aws'
  const nomeLimpo = nome.trim()
  const slugValido = SLUG_PROJETO.test(slug)
  const justificativaLimpa = justificativa.trim()
  const justificativaOk =
    justificativaLimpa.length >= JUSTIFICATIVA_MIN && justificativaLimpa.length <= JUSTIFICATIVA_MAX

  // O objeto tem de ser estável: o debounce reinicia a cada valor novo.
  const consultaAgora = useMemo(
    () => ({ tipo, nome: nomeLimpo, project_slug: semAws ? '' : slug }), [tipo, nomeLimpo, semAws, slug])
  const [consulta] = useDebouncedValue(consultaAgora, 400)
  const consultaPronta =
    consulta.nome !== '' && (consulta.tipo === 'sem_aws' || SLUG_PROJETO.test(consulta.project_slug))
  const prontidao = useQuery({
    queryKey: ['pedido-org-prontidao', tenantId, consulta.tipo, consulta.nome, consulta.project_slug],
    queryFn: () => lerProntidao(tenantId, consulta),
    enabled: opened && desfecho == null && consultaPronta,
    retry: false,
  })

  const enviar = useMutation({
    mutationFn: () => abrirPedido(tenantId, {
      tipo, nome: nomeLimpo, project_slug: semAws ? '' : slug,
      responsavel_user_id: responsavel ? Number(responsavel) : null,
      justificativa: justificativaLimpa,
    }),
    onSuccess: (pedido) => { setErro(null); setDesfecho({ tipo: 'pedido', pedido }) },
    onError: (err) => setErro(motivoDaRecusa(err, 'Não foi possível enviar o pedido.')),
  })

  const criarOrg = useCreateOrganization()
  const criar = () => {
    setErro(null)
    criarOrg.mutate({
      name: nomeLimpo,
      description: justificativaLimpa,
      responsible_user_id: Number(responsavel),
      criar_conta_prd: tipo === 'projeto_novo',
      ...(semAws || !slug ? {} : { project_slug: slug }),
    }, {
      onSuccess: (org) => setDesfecho({ tipo: 'criada', nome: org.name, conta: org.conta_prd }),
      onError: (err) => setErro(motivoDaRecusa(err, 'Não foi possível criar a organização.')),
    })
  }

  const fechar = () => {
    setNome('')
    setSlug('')
    setSlugEditado(false)
    setResponsavel(null)
    setJustificativa('')
    setErro(null)
    setDesfecho(null)
    onClose()
  }

  const recusada = prontidao.data?.pode_enviar === false
  const podeConfirmar =
    nomeLimpo !== '' && (semAws || slugValido) && responsavel != null && justificativaOk && !recusada

  return (
    <Modal opened={opened} onClose={fechar} title="Nova Organização" size="lg">
      {desfecho ? (
        <Stack gap="sm">
          {desfecho.tipo === 'pedido' ? (
            <Alert color="teal" title="Pedido enviado">
              <Text size="sm">
                Protocolo <b>{desfecho.pedido.protocolo}</b>. O pedido virou um chamado neste tenant, atendido
                pela DEVOPS. Você acompanha a decisão por ele.
              </Text>
              <Anchor component={Link} to={`/chamados/${desfecho.pedido.task_request_id}`} size="sm" onClick={fechar}>
                Abrir o chamado
              </Anchor>
            </Alert>
          ) : (
            <Alert color={desfecho.conta?.erro ? 'yellow' : 'teal'} title="Organização criada">
              <Text size="sm">Organização &quot;{desfecho.nome}&quot; criada.</Text>
              {desfecho.conta?.job_id != null && (
                <Text size="sm">
                  Conta de produção em criação (
                  <Anchor href={`/admin/tenants/jobs/${desfecho.conta.job_id}`} size="sm">job #{desfecho.conta.job_id}</Anchor>
                  ).
                </Text>
              )}
              {desfecho.conta?.erro && (
                <Text size="sm">A conta de produção não foi pedida: {desfecho.conta.erro}</Text>
              )}
            </Alert>
          )}
          <Group justify="flex-end"><Button variant="default" onClick={fechar}>Fechar</Button></Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          {acao === 'criar' && (
            <Alert color="blue">
              Você pode criar a organização direto. A permissão é conferida no tenant em que você está, e vale
              para a plataforma inteira: a organização nova não pertence a este tenant.
            </Alert>
          )}
          {tipos.length > 1 && (
            <Select
              label="Tipo"
              data={tipos.map((t) => ({ value: t, label: ROTULO_DO_TIPO[t] ?? t }))}
              value={tipo}
              onChange={(v) => v && setTipo(v as TipoDePedido)}
              allowDeselect={false}
              description={
                tipo === 'conta_existente'
                  ? 'Você só avisa que o projeto já existe; quem associa a conta à organização é o atendente.'
                  : undefined
              }
            />
          )}
          <TextInput
            label="Nome da organização"
            value={nome}
            required
            onChange={(e) => {
              const v = e.currentTarget.value
              setNome(v)
              if (!slugEditado) setSlug(slugDoNome(v))
            }}
          />
          {!semAws && (
            <CampoSlugDoProjeto
              valor={slug}
              onChange={(v) => { setSlugEditado(true); setSlug(v) }}
              aviso={tipo === 'conta_existente'
                ? `O slug é o nome do projeto que já existe na AWS (${slug}). O slug não muda depois.`
                : undefined}
            />
          )}
          <Select
            label="Responsável"
            placeholder="Selecione o gestor responsável"
            data={(gestores ?? []).map((g) => ({ value: String(g.id), label: `${g.full_name} (${g.email})` }))}
            value={responsavel}
            onChange={setResponsavel}
            searchable
            required
            withAsterisk
            description={
              gestores && !quemPedeEGestor
                ? 'Escolha alguém da lista: o Responsável precisa ter a função gestor.'
                : undefined
            }
          />
          <Textarea
            label="Justificativa"
            value={justificativa}
            onChange={(e) => setJustificativa(e.currentTarget.value)}
            required
            autosize
            minRows={2}
            maxLength={JUSTIFICATIVA_MAX}
            description={`De ${JUSTIFICATIVA_MIN} a ${JUSTIFICATIVA_MAX} caracteres (${justificativaLimpa.length}/${JUSTIFICATIVA_MAX}).`}
          />

          <Prontidao carregando={prontidao.isFetching} dados={prontidao.data} erro={prontidao.error} />

          {erro && <Alert color="red">{erro}</Alert>}

          <Group justify="flex-end">
            <Button variant="default" onClick={fechar}>Cancelar</Button>
            {acao === 'criar' ? (
              <Button onClick={criar} loading={criarOrg.isPending} disabled={!podeConfirmar}>Criar</Button>
            ) : (
              <Button onClick={() => enviar.mutate()} loading={enviar.isPending} disabled={!podeConfirmar}>
                Enviar pedido
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  )
}

function Prontidao({ carregando, dados, erro }: {
  carregando: boolean
  dados: ProntidaoDoPedido | undefined
  erro: unknown
}) {
  if (erro) {
    return <Alert color="gray">{motivoDaRecusa(erro, 'Não foi possível conferir agora; o envio confere de novo.')}</Alert>
  }
  if (!dados) {
    return carregando ? <Group gap="xs"><Loader size="xs" /><Text size="xs" c="dimmed">Conferindo…</Text></Group> : null
  }
  return (
    <Stack gap="xs">
      <Text size="sm" fw={600}>Conferido agora</Text>
      <List spacing={4} size="sm">
        {dados.conferencias.map((c) => (
          <List.Item
            key={c.item}
            icon={
              <ThemeIcon color={c.ok ? 'teal' : 'red'} size={16} radius="xl">
                {c.ok ? <IconCheck size={11} /> : <IconX size={11} />}
              </ThemeIcon>
            }
          >
            {c.motivo}
          </List.Item>
        ))}
      </List>
      {dados.recusas.length > 0 && (
        <Alert color="red" title="O pedido não pode ser enviado assim">
          <Stack gap={4}>
            {dados.recusas.map((r) => <Text key={r.codigo} size="sm">{r.motivo}</Text>)}
          </Stack>
        </Alert>
      )}
      {dados.depende_de_terceiro.length > 0 && (
        <Alert color="yellow" title="Depende de terceiro">
          <Stack gap={6}>
            <Text size="xs" c="dimmed">Não impede o envio: é o que outra pessoa precisa fazer para a criação ir até o fim.</Text>
            {dados.depende_de_terceiro.map((t) => (
              <Stack key={t.item} gap={2}>
                <Text size="sm" fw={600}>{ROTULO_DO_TERCEIRO[t.item] ?? t.item}</Text>
                <Text size="sm">{t.motivo}</Text>
                {t.acao && <Text size="sm">O que fazer: {t.acao}</Text>}
              </Stack>
            ))}
          </Stack>
        </Alert>
      )}
    </Stack>
  )
}
